/**
 * Helpers for account calendar: month range, grid, and date grouping.
 */

/** Format a Date as YYYY-MM-DD using the browser's local timezone. */
export function toLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Aggregate activity rows by local calendar day from each row's `occurred_at`
 * (same semantics as the activity feed list and mini calendar).
 */
export function historyItemsToLocalDayCounts(
  items: ReadonlyArray<{ occurred_at: string }> | null | undefined
): Record<string, number> {
  if (!items?.length) return {};
  const out: Record<string, number> = {};
  for (const item of items) {
    const t = item.occurred_at;
    if (t == null || t === "") continue;
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) continue;
    const key = toLocalDateKey(d);
    out[key] = (out[key] ?? 0) + 1;
  }
  return out;
}

/** Local calendar date at midnight (avoids DST / time drift when comparing days). */
export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Monday (local) for the week containing `date`. Week is Mon–Sun (day 0 = Sunday maps to previous Monday).
 */
export function getMondayOfCalendarWeek(date: Date): Date {
  const local = startOfLocalDay(date);
  const dow = local.getDay();
  const daysFromMonday = dow === 0 ? 6 : dow - 1;
  local.setDate(local.getDate() - daysFromMonday);
  return local;
}

/** Add calendar days in local timezone. */
export function addLocalDays(date: Date, deltaDays: number): Date {
  const d = startOfLocalDay(date);
  d.setDate(d.getDate() + deltaDays);
  return d;
}

export function parseLocalDateKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Inclusive local date keys → API `since` (inclusive) / `until` (exclusive end of last day). */
export function buildCommittedLocalDayRange(
  rangeStart: string,
  rangeEnd: string
): { since: string; until: string } {
  const minDate = rangeStart <= rangeEnd ? rangeStart : rangeEnd;
  const maxDate = rangeStart <= rangeEnd ? rangeEnd : rangeStart;
  const start = startOfLocalDay(parseLocalDateKey(minDate));
  const untilExclusive = startOfLocalDay(parseLocalDateKey(maxDate));
  untilExclusive.setDate(untilExclusive.getDate() + 1);
  return { since: start.toISOString(), until: untilExclusive.toISOString() };
}

/**
 * Local calendar date for the upcoming Sunday that ends the “today → Sunday” frozen-week window
 * (matches a Sun–Sat header row: if today is Sunday, returns today).
 */
export function getFrozenWeekWindowEndDate(from: Date = new Date()): Date {
  const local = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const dow = local.getDay();
  const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
  local.setDate(local.getDate() + daysUntilSunday);
  return local;
}

export function getFrozenWeekWindowEndKey(from: Date = new Date()): string {
  return toLocalDateKey(getFrozenWeekWindowEndDate(from));
}

/** True when `dateKey` is between local today and the upcoming Sunday (inclusive). */
export function isDateKeyInFrozenWeekWindow(dateKey: string, from: Date = new Date()): boolean {
  const startKey = toLocalDateKey(from);
  const endKey = getFrozenWeekWindowEndKey(from);
  return dateKey >= startKey && dateKey <= endKey;
}

/**
 * **Daily Actions week strip + account calendar month grid** — show lock + “frozen” styling on a day.
 * Same as {@link isDateKeyInFrozenWeekWindow} (today through Sunday) **plus** earlier days in the
 * same Mon–Sun week (Monday up to yesterday).
 */
export function isDateKeyDailyActionsLockedStripDay(dateKey: string, from: Date = new Date()): boolean {
  if (isDateKeyInFrozenWeekWindow(dateKey, from)) return true;
  const todayStart = startOfLocalDay(from);
  const todayKey = toLocalDateKey(todayStart);
  const monKey = toLocalDateKey(getMondayOfCalendarWeek(todayStart));
  return dateKey >= monKey && dateKey < todayKey;
}

/** Start and end of month as ISO date strings (YYYY-MM-DD). */
export function getMonthRange(date: Date): { startDate: string; endDate: string } {
  const year = date.getFullYear();
  const month = date.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  return { startDate: toLocalDateKey(first), endDate: toLocalDateKey(last) };
}

/**
 * Full local calendar month (1st → last day) for the month containing `viewDate`.
 * `now` is accepted for backward compatibility; it is not used.
 */
export function defaultRangeForView(viewDate: Date, _now?: Date): { start: string; end: string } {
  const y = viewDate.getFullYear();
  const m = viewDate.getMonth();
  const firstOfMonth = new Date(y, m, 1);
  const lastOfMonth = new Date(y, m + 1, 0);
  return { start: toLocalDateKey(firstOfMonth), end: toLocalDateKey(lastOfMonth) };
}

/** True when the committed range equals {@link defaultRangeForView} for `referenceDay`. */
export function isDefaultFullMonthForReferenceDay(
  rangeStart: string | null,
  rangeEnd: string | null,
  referenceDay: Date
): boolean {
  if (rangeStart === null || rangeEnd === null) return false;
  const dr = defaultRangeForView(referenceDay);
  return rangeStart === dr.start && rangeEnd === dr.end;
}

/** Local Monday–Sunday (inclusive) for the week containing `referenceDay` (same week model as daily actions lock strip). */
export function defaultLockedWeekRangeForReferenceDay(referenceDay: Date): { start: string; end: string } {
  const monday = getMondayOfCalendarWeek(referenceDay);
  const sunday = addLocalDays(monday, 6);
  return { start: toLocalDateKey(monday), end: toLocalDateKey(sunday) };
}

/** True when the range is exactly {@link defaultLockedWeekRangeForReferenceDay} for `referenceDay`. */
export function isDefaultLockedWeekForReferenceDay(
  rangeStart: string | null,
  rangeEnd: string | null,
  referenceDay: Date
): boolean {
  if (rangeStart === null || rangeEnd === null) return false;
  const dr = defaultLockedWeekRangeForReferenceDay(referenceDay);
  return rangeStart === dr.start && rangeEnd === dr.end;
}

function minLocalDateKeys(a: string, b: string): [string, string] {
  return a <= b ? [a, b] : [b, a];
}

/**
 * Short label for a YYYY-MM-DD range (e.g. `Apr 3 – Apr 15`), matching the mini calendar range row.
 * When `rangeEnd` is null, only `rangeStart` is shown.
 */
export function formatLocalDateRangeLabel(rangeStart: string | null, rangeEnd: string | null): string {
  if (rangeStart === null) return "";
  const end = rangeEnd ?? rangeStart;
  const [lo, hi] = minLocalDateKeys(rangeStart, end);
  const fmt = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (lo === hi) return fmt(lo);
  return `${fmt(lo)} – ${fmt(hi)}`;
}

/**
 * First and last calendar cell for the month grid (includes leading/trailing days from adjacent months).
 * Use this for API date ranges so every visible day can show plan/history data.
 */
export function getVisibleCalendarGridRange(
  date: Date,
  options?: CalendarGridOptions
): { startDate: string; endDate: string } {
  const grid = getCalendarGrid(date, options);
  if (grid.length === 0) return getMonthRange(date);
  return {
    startDate: grid[0].dateKey,
    endDate: grid[grid.length - 1].dateKey,
  };
}

/**
 * Plan/history fetch window: union of the month grid (mini calendar’s viewed month) and the committed
 * date range. Otherwise changing only the viewed month would refetch a different slice and the list view
 * would change even when `rangeStart`/`rangeEnd` are unchanged.
 */
export function getAccountCalendarDataFetchRange(
  calendarMonth: Date,
  rangeStart: string | null,
  rangeEnd: string | null,
  gridOptions?: CalendarGridOptions
): { startDate: string; endDate: string } {
  const grid = getVisibleCalendarGridRange(calendarMonth, gridOptions);
  if (rangeStart === null) return grid;
  const endKey = rangeEnd ?? rangeStart;
  const lo = rangeStart <= endKey ? rangeStart : endKey;
  const hi = rangeStart <= endKey ? endKey : rangeStart;
  return {
    startDate: lo < grid.startDate ? lo : grid.startDate,
    endDate: hi > grid.endDate ? hi : grid.endDate,
  };
}

export interface CalendarDay {
  date: Date;
  dateKey: string; // YYYY-MM-DD
  isCurrentMonth: boolean;
  dayOfWeek: number; // 0 = Sunday
}

export interface CalendarGridOptions {
  /** 0 = Sunday (default), 1 = Monday. */
  weekStart?: 0 | 1;
  /** When true, always produce 6 rows (42 cells) for consistent height. */
  fixedRows?: boolean;
}

/**
 * Returns a grid of days for the given month, including leading/trailing days from adjacent months.
 * By default the first row starts on Sunday (weekStart=0). Pass weekStart=1 for Monday start.
 * Uses minimum rows (5 or 6) unless fixedRows=true, which always produces 6 rows (42 cells).
 */
export function getCalendarGrid(date: Date, options?: CalendarGridOptions): CalendarDay[] {
  const weekStart = options?.weekStart ?? 0;
  const fixedRows = options?.fixedRows ?? false;

  const year = date.getFullYear();
  const month = date.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay(); // 0 = Sunday
  const daysInMonth = last.getDate();

  const result: CalendarDay[] = [];

  const leadingCount = (startDay - weekStart + 7) % 7;

  const prevMonthLast = new Date(year, month, 0);
  for (let i = leadingCount - 1; i >= 0; i--) {
    const d = new Date(prevMonthLast);
    d.setDate(d.getDate() - i);
    result.push({
      date: d,
      dateKey: toLocalDateKey(d),
      isCurrentMonth: false,
      dayOfWeek: d.getDay(),
    });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month, day);
    result.push({
      date: d,
      dateKey: toLocalDateKey(d),
      isCurrentMonth: true,
      dayOfWeek: d.getDay(),
    });
  }

  const totalCells = leadingCount + daysInMonth;
  const rowCount = fixedRows ? 6 : Math.ceil(totalCells / 7);
  const gridSize = rowCount * 7;
  const remaining = gridSize - totalCells;
  if (remaining > 0) {
    const nextMonthFirst = new Date(year, month + 1, 1);
    for (let i = 0; i < remaining; i++) {
      const d = new Date(nextMonthFirst);
      d.setDate(d.getDate() + i);
      result.push({
        date: d,
        dateKey: toLocalDateKey(d),
        isCurrentMonth: false,
        dayOfWeek: d.getDay(),
      });
    }
  }
  return result;
}

/** Extract YYYY-MM-DD from suggested_date, suggested_at, or occurred_at. */
export function getDateKeyFromIso(isoString: string | null): string | null {
  if (!isoString) return null;
  return isoString.slice(0, 10);
}
